import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import type { ComponentType, ReactNode } from 'react';
import type { SubNavItem } from './helpers';

// Shared shell for a full-size nav row (top-level items and the "Staff" disclosure) - active
// state suppresses the hover classes entirely so hovering an active row doesn't flash gray.
function navItemClasses(active: boolean, extra?: string): string {
  return cn(
    'group relative z-1 flex items-center rounded-lg border border-transparent p-1.5 font-medium text-sm transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-spark-accent-hover focus-visible:ring-offset-2 focus-visible:ring-offset-spark-surface-subtle',
    extra,
    active
      ? 'border-spark-accent-border! bg-spark-accent-soft text-spark-accent'
      : 'hover:bg-spark-surface-hover hover:text-spark-accent',
  );
}

type NavDisclosureButtonProps = {
  icon?: ComponentType<{ className?: string }>;
  title: string;
  active: boolean;
  expanded: boolean;
  onClick: () => void;
};

/** The toggle button for a nav item with a sub-nav (or the "Staff" section) - icon, label and a
 * chevron that flips when expanded. */
export function NavDisclosureButton({
  icon: Icon,
  title,
  active,
  expanded,
  onClick,
}: NavDisclosureButtonProps) {
  return (
    <button
      type="button"
      className={navItemClasses(active, 'w-full cursor-pointer')}
      onClick={onClick}
    >
      {Icon && <Icon className="mr-2 size-4 shrink-0 group-hover:text-spark-accent" />}
      <span className="flex-1 text-left">{title}</span>
      <ChevronDownIcon
        className={cn(
          'size-3.5 text-spark-text-tertiary transition-transform duration-200 group-hover:text-spark-accent-hover',
          expanded && 'rotate-180',
        )}
      />
    </button>
  );
}

type NavLinkProps = {
  href: string;
  title: string;
  icon?: ComponentType<{ className?: string }>;
  active: boolean;
  onNavigate: (event: React.MouseEvent, href: string) => void;
};

/** A top-level nav item with no sub-nav of its own. */
export function NavLink({ href, title, icon: Icon, active, onNavigate }: NavLinkProps) {
  return (
    <a href={href} onClick={(event) => onNavigate(event, href)} className={navItemClasses(active)}>
      {Icon && <Icon className="mr-2 size-4 shrink-0 group-hover:text-spark-accent" />}
      <span>{title}</span>
    </a>
  );
}

/** Collapsible wrapper for a nav item's sub-nav list, animated via grid-template-rows. */
export function NavSubMenu({ expanded, children }: { expanded: boolean; children: ReactNode }) {
  return (
    <div
      className={cn(
        'grid transition-[grid-template-rows] duration-200 ease-linear',
        expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      )}
      // TODO: remove this? it breaks clicking children
      // inert={(!expanded).toString() as 'true' | 'false'}
    >
      <div className="overflow-hidden">{children}</div>
    </div>
  );
}

type SubNavLinkProps = {
  item: SubNavItem;
  active: boolean;
  onNavigate: (event: React.MouseEvent, href: string) => void;
};

/** One row inside a `NavSubMenu` - shared by the core nav's sub-nav lists and the staff nav. */
export function SubNavLink({ item, active, onNavigate }: SubNavLinkProps) {
  return (
    <a
      href={item.href}
      onClick={(event) => onNavigate(event, item.href)}
      className={cn(
        'group relative z-1 flex cursor-pointer items-center rounded-lg border border-transparent py-1 pr-1 pl-1.5 text-sm outline-spark-accent-hover transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2',
        active
          ? 'border-spark-accent-border! bg-spark-accent-soft font-medium text-spark-accent hover:bg-spark-accent-soft'
          : 'hover:bg-spark-surface-hover hover:text-spark-accent',
      )}
    >
      <span className="mr-1.5 size-5" />
      <span>{item.title}</span>
    </a>
  );
}
